import React, { useMemo } from 'react';
import { Spin } from 'antd';
import moment from 'moment';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import AnalyticsChartHeader from '../analytics_charts/AnalyticsChartHeader';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	CategoryBreakdownBarChart,
	computeCategoryBreakdownChartHeight,
	buildInterleavedComparisonChartData,
	pairComparisonByFieldName,
	resolveCategoryBreakdownChartPadding,
} from '../analytics_charts';
import { useAnalyticsBreakdownComparison } from '../../hooks/useAnalyticsBreakdownComparison';
import {
	COMPARISON_PERIOD_COLOR,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	getAnalyticsRolePrimaryColor,
} from '../../utils/AnalyticsComparisonUtils';
import { buildSeriesComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import { exportBreakdownCsv, BreakdownExportRow } from '../../utils/AnalyticsBreakdownExportUtils';

export type AnalyticsBreakdownChartSectionProps = {
	loading?: boolean;
	filters: Record<string, any>;
	userType: string;
	primaryChartRows: BreakdownExportRow[];
	fetchComparisonData: (params: Record<string, any>) => Promise<any[]>;
	normalizeComparisonData?: (rawData: any[]) => any[];
	/** Pass when comparison rows depend on client-side search or tree selection filters. */
	localFilterRevision?: string | number;
	categoryField?: string;
	valueField?: string;
	cols?: any;
	formatValueLabel: (val: any) => string;
	valueAxisTitle: string;
	exportTitle: string;
	exportCategoryLabel: string;
	exportValueLabel: string;
	formatExportValue: (amount: number) => string | number;
	singleTooltipItemTemplate?: string;
	chartPadding?: [number, number, number, number];
	/** Chart card title label (e.g., "Spend by Problem Type"). Renders alongside `chartTitleValue` using the shared AnalyticsChartHeader. */
	chartTitleLabel?: string;
	/** Pre-formatted total for the chart card title (e.g., "$42,000" or "128 Work Orders"). Only rendered when chartTitleLabel is provided. */
	chartTitleValue?: string;
};

const AnalyticsBreakdownChartSection = ({
	loading,
	filters,
	userType,
	primaryChartRows,
	fetchComparisonData,
	normalizeComparisonData,
	localFilterRevision,
	categoryField = 'fieldName',
	valueField = 'subTotal',
	cols,
	formatValueLabel,
	valueAxisTitle,
	exportTitle,
	exportCategoryLabel,
	exportValueLabel,
	formatExportValue,
	singleTooltipItemTemplate,
	chartPadding,
	chartTitleLabel,
	chartTitleValue,
}: AnalyticsBreakdownChartSectionProps) => {
	const currentColor = getAnalyticsRolePrimaryColor(userType);
	const previousColor = COMPARISON_PERIOD_COLOR;

	const {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison,
	} = useAnalyticsBreakdownComparison({
		filters,
		fetchComparisonData,
		normalizeComparisonData,
		localFilterRevision,
	});

	const { chartData, categoryOrder } = useMemo(() => {
		const paired = pairComparisonByFieldName({
			current: primaryChartRows || [],
			previous: comparisonRows || [],
			hasComparison,
			matchField: categoryField,
		});
		return {
			chartData: hasComparison
				? buildInterleavedComparisonChartData(paired, categoryField)
				: paired.current,
			categoryOrder: paired.categoryOrder,
		};
	}, [primaryChartRows, comparisonRows, hasComparison, categoryField]);

	// In comparison mode `chartData` is the union of current and previous categories, so previous-only
	// categories add bands the chart must make room for. Size from the distinct categories actually
	// rendered (not just `primaryChartRows`) or those extra rows get clipped by the height cap.
	const categoryCount = useMemo(() => {
		const seenCategories = new Set<string>();
		chartData.forEach((row) => {
			const category = row && row[categoryField];
			if (category !== null && category !== undefined) {
				seenCategories.add(String(category));
			}
		});
		return seenCategories.size;
	}, [chartData, categoryField]);
	const effectiveChartPadding = resolveCategoryBreakdownChartPadding(hasComparison, chartPadding);
	const chartHeight = computeCategoryBreakdownChartHeight(
		categoryCount,
		hasComparison,
		effectiveChartPadding
	);

	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const categoryTitle =
			title || (items[0] && items[0].data && items[0].data[categoryField]) || '';
		const sortedItems = items
			.slice()
			.sort((itemA, itemB) => Number(isCurrentItem(itemB)) - Number(isCurrentItem(itemA)));
		const rows = sortedItems.map((item) => {
			const isCurrent = isCurrentItem(item);
			return {
				label: isCurrent ? 'Current Period:' : 'Previous Period:',
				color: isCurrent ? currentColor : previousColor,
				value: formatValueLabel((item.data && item.data[valueField]) || 0),
			};
		});
		return buildSeriesComparisonTooltipHtml(rows, { minWidth: 260, title: categoryTitle });
	};

	const handleDownload = () => {
		exportBreakdownCsv({
			title: exportTitle,
			categoryColumnLabel: exportCategoryLabel,
			valueColumnLabel: exportValueLabel,
			currentRows: primaryChartRows,
			comparisonRows: hasComparison ? comparisonRows : undefined,
			comparisonState,
			formatValue: formatExportValue,
		});
	};

	if (!primaryChartRows || primaryChartRows.length === 0) {
		return null;
	}

	const extra = (
		<AnalyticsChartActions
			comparison={{
				value: comparisonState,
				onChange: setComparison,
				selectedStart,
				selectedEnd,
				isTimeBased: true,
				loading: comparisonLoading,
				disabled: comparisonDisabled,
				disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
			}}
			onDownload={handleDownload}
		/>
	);

	const titleNode = chartTitleLabel ? (
		<AnalyticsChartHeader label={chartTitleLabel} value={chartTitleValue} />
	) : null;

	return (
		<AnalyticsChartCard title={titleNode} extra={extra} variant="flush" className="mt-2">
			<Spin spinning={!!loading || comparisonLoading}>
				<div className={hasComparison ? 'pb-1' : undefined}>
					<CategoryBreakdownBarChart
						data={chartData}
						hasComparison={hasComparison}
						categoryOrder={categoryOrder}
						currentColor={currentColor}
						previousColor={previousColor}
						height={chartHeight}
						chartPadding={effectiveChartPadding}
						categoryField={categoryField}
						valueField={valueField}
						cols={cols}
						formatValueLabel={formatValueLabel}
						valueAxisTitle={valueAxisTitle}
						renderComparisonTooltip={renderComparisonTooltip}
						singleTooltipItemTemplate={singleTooltipItemTemplate}
					/>
				</div>
				{hasComparison ? (
					<ComparisonLegend
						currentStart={selectedStart}
						currentEnd={selectedEnd}
						previousStart={comparisonStart}
						previousEnd={comparisonEnd}
						currentColor={currentColor}
						previousColor={previousColor}
						mode={comparisonState.mode}
						swatchType="square"
					/>
				) : (
					<div className="mt-2 h-5" aria-hidden="true" />
				)}
			</Spin>
		</AnalyticsChartCard>
	);
};

export default AnalyticsBreakdownChartSection;
